import { ref, computed, watch } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useSurveyStore } from "@stores/survey/survey";
import { useResultsV2Store } from "./resultsV2";
import useMyFetch from "@stores/useMyFetch";
import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
import { togglePopulationFilter } from "@/utils/togglePopulationFilter";
import { captureEvent } from "@/lib/analytics";

export const useResultsV2PopulationsStore = defineStore("resultsV2Populations", () => {
  const { token: surveyToken } = storeToRefs(useSurveyStore());

  const populations = ref([]);

  function populationsPath(viewToken) {
    return `/surveys/${surveyToken.value}/results_views/${viewToken}/results_view_populations`;
  }

  function resetForView() {
    populations.value = [];
  }

  async function fetchPopulations(viewToken) {
    // Ephemeral populations are client-side only: keep them across refetches.
    const ephemerals = populations.value.filter((p) => p.ephemeral);
    if (!viewToken) {
      populations.value = ephemerals;
      return populations.value;
    }
    const { response, data } = await useMyFetch(populationsPath(viewToken)).get().json();
    if (!response.value.ok) return [];
    populations.value = [...(data.value || []), ...ephemerals];
    return populations.value;
  }

  async function addPopulation({ search_group_id }) {
    const resultsV2Store = useResultsV2Store();
    const viewToken = resultsV2Store.currentView?.token;
    if (!viewToken) return null;

    if (resultsV2Store.isDefaultView) {
      const searchGroup = resultsV2Store.savedPopulations.find((sg) => sg.id === search_group_id);
      if (!searchGroup) return null;
      const localPop = { id: `local_${search_group_id}`, position: populations.value.length + 1, search_group: searchGroup };
      populations.value.push(localPop);
      return localPop;
    }

    const { response, data } = await useMyFetch(populationsPath(viewToken))
      .post({ results_view_population: { search_group_id } })
      .json();
    if (!response.value.ok || !data.value) return null;
    populations.value.push(data.value);

    captureEvent("results_view_population_added", { view_token: viewToken });

    return data.value;
  }

  async function removePopulation(populationId) {
    const resultsV2Store = useResultsV2Store();
    const viewToken = resultsV2Store.currentView?.token;
    const pop = populations.value.find((p) => p.id === populationId);

    // Ephemeral populations live only client-side — never hit the server.
    if (pop?.ephemeral || resultsV2Store.isDefaultView) {
      populations.value = populations.value.filter((p) => p.id !== populationId);
      return;
    }

    if (!viewToken) return;
    const { response } = await useMyFetch(`${populationsPath(viewToken)}/${populationId}`).delete();
    if (!response.value.ok) return;
    populations.value = populations.value.filter((p) => p.id !== populationId);

    captureEvent("results_view_population_removed", { view_token: viewToken });
  }

  // Local-only cleanup for when the search group is destroyed server-side
  // (the API cascades the results_view_populations deletion).
  function removeBySearchGroupId(searchGroupId) {
    const sizeBefore = populations.value.length;
    populations.value = populations.value.filter((p) => p.search_group?.id !== searchGroupId);
    return populations.value.length !== sizeBefore;
  }

  // --- Quick-filter (temporary) population ---
  // A bar click on "All" builds an in-place "All*" quick filter (same name + colour),
  // never persisted until saved.

  let ephemeralSeq = 0;

  function hasEphemeral() {
    return populations.value.some((p) => p.ephemeral);
  }

  // Only from-All quick filters cause "All" to step aside; standalone (New) populations
  // are independent and must not keep "All" hidden.
  const allQuickFilterActive = computed(() =>
    populations.value.some((p) => p.ephemeral && p._fromAll),
  );

  // "All" steps aside only while a from-All quick filter exists; as soon as the quick
  // filter disappears (reset, promote, removal), the untouched "All" comes back.
  watch(allQuickFilterActive, (active) => {
    if (!active) useResultsV2Store().allTemporarilyHidden = false;
  });

  function nextGroupName() {
    // Skip names already used in the view AND in the saved library: the server
    // rejects duplicate search_group names, which would break promotion later.
    const used = new Set([
      ...populations.value.map((p) => p.search_group?.name),
      ...useResultsV2Store().savedPopulations.map((sg) => sg.name),
    ]);
    for (let i = 0; i < 26; i++) {
      const name = `Pop ${String.fromCharCode(65 + i)}`;
      if (!used.has(name)) return name;
    }
    return `Pop ${populations.value.length + 1}`;
  }

  function buildEphemeral({ name, color, filters }) {
    ephemeralSeq += 1;
    const token = `eph_${ephemeralSeq}`;
    return {
      id: token,
      position: populations.value.length + 1,
      ephemeral: true,
      hidden: false,
      search_group: {
        id: token,
        token,
        name: name || nextGroupName(),
        color: color || POPULATION_AUTO_COLORS[(populations.value.length + 1) % POPULATION_AUTO_COLORS.length],
        ephemeral: true,
        filters,
      },
    };
  }

  function createEphemeral({ name, color, filters } = {}) {
    const pop = buildEphemeral({ name, color, filters: filters || {} });
    populations.value.push(pop);
    return pop;
  }

  // Edit a population in place — applied live, never persisted (dirtiness is derived
  // by comparing against the saved search_group).
  function updatePopulation(populationId, { name, color, filters }) {
    const pop = populations.value.find((p) => p.id === populationId);
    if (!pop) return null;
    pop.search_group = {
      ...pop.search_group,
      name: name ?? pop.search_group.name,
      color: color !== undefined ? color : pop.search_group.color,
      filters: filters ?? pop.search_group.filters,
    };
    return pop;
  }

  // Toggle a question=code condition inside one population's filters (OR within a
  // question, AND across questions). `code` may be an array (e.g. an NPS bucket),
  // toggled as one unit: all present → all removed, otherwise all set.
  function toggleConditionOn(pop, questionId, code) {
    const filters = togglePopulationFilter(pop.search_group.filters, questionId, code);

    // A from-All quick filter with no conditions left disappears and "All" comes back.
    if (pop.ephemeral && pop._fromAll && Object.keys(filters).length === 0) {
      populations.value = populations.value.filter((p) => p.id !== pop.id);
      return { removed: true };
    }

    pop.search_group = { ...pop.search_group, filters };
    return { removed: false };
  }

  // Bar-click flow. `target` is "all" or a view-population id; filtering happens in place,
  // the clicked population keeping its slot. Returns { created } for a fresh "All*".
  function toggleFilterOnPopulation({ target, questionId, code }) {
    if (target === "all") {
      const existing = populations.value.find((p) => p.ephemeral && p._fromAll);
      if (existing) {
        toggleConditionOn(existing, questionId, code);
        return { created: false };
      }
      // Kept in All's (front) slot so the population order never shifts.
      const pop = buildEphemeral({
        name: "All",
        color: POPULATION_AUTO_COLORS[0],
        filters: { [questionId]: Object.fromEntries((Array.isArray(code) ? code : [code]).map((c) => [c, true])) },
      });
      pop._fromAll = true;
      populations.value.unshift(pop);
      return { created: true };
    }

    const pop = populations.value.find((p) => p.id === target);
    if (!pop) return { created: false };
    toggleConditionOn(pop, questionId, code);
    return { created: false };
  }

  function savedSearchGroupFor(pop) {
    return useResultsV2Store().savedPopulations.find((s) => s.id === pop.search_group?.id) || null;
  }

  // Whether a saved population's in-memory state (name/color/filters) drifts from what's
  // persisted — i.e. it carries unsaved edits/quick filters.
  function isModifiedFromSaved(pop) {
    const saved = savedSearchGroupFor(pop);
    if (!saved) return false;
    const sg = pop.search_group || {};
    return (
      (sg.name || "") !== (saved.name || "") ||
      (sg.color || null) !== (saved.color || null) ||
      JSON.stringify(sg.filters || {}) !== JSON.stringify(saved.filters || {})
    );
  }

  // Modified & unsaved: a quick filter, or a saved population edited in place.
  function isDirty(pop) {
    return !!pop.ephemeral || isModifiedFromSaved(pop);
  }

  function hasFilters(pop) {
    return Object.keys(pop.search_group?.filters || {}).length > 0;
  }

  // Dirty AND actually narrowed: drives the "*" marker. A fresh blank population
  // (ephemeral, no filters yet) is unsaved but not narrowed, so it stays "Pop A".
  function isNarrowed(pop) {
    return isDirty(pop) && hasFilters(pop);
  }

  // Reset reverts a population to its baseline:
  // - a from-All quick filter is a transient narrowing of "All", so it disappears and "All" returns;
  // - a standalone (New) population reverts to blank = all respondents, keeping its pill;
  // - a saved population reverts to its saved name/color/filters.
  async function resetPopulation(populationId) {
    const pop = populations.value.find((p) => p.id === populationId);
    if (!pop) return;
    if (pop.ephemeral) {
      if (pop._fromAll) {
        await removePopulation(pop.id);
      } else {
        pop.search_group = { ...pop.search_group, filters: {} };
      }
      return;
    }
    const saved = savedSearchGroupFor(pop);
    if (saved) {
      pop.search_group = {
        ...pop.search_group,
        name: saved.name,
        color: saved.color || null,
        filters: JSON.parse(JSON.stringify(saved.filters || {})),
      };
    }
  }

  // A quick filter is promoted to a new population; a saved population is updated in place.
  async function savePopulation(populationId) {
    const pop = populations.value.find((p) => p.id === populationId);
    if (!pop) return null;
    if (pop.ephemeral) return promoteEphemeral(pop.id);

    const sg = pop.search_group;
    const updated = await useResultsV2Store().updateSavedPopulation(sg.token, {
      name: sg.name || "Unnamed",
      filters: sg.filters,
      color: sg.color || null,
    });
    if (!updated) return null;
    populations.value.forEach((p) => {
      if (p.search_group?.id === updated.id) p.search_group = updated;
    });
    return updated;
  }

  // Results V1-style feedback: "All" steps aside while the quick filter is on (it comes
  // back on Reset — see the allQuickFilterActive watcher). Called once the quick
  // population's data is ready, so the chart bars morph in place instead of blanking.
  function hideAllAfterQuickFilter() {
    if (allQuickFilterActive.value) useResultsV2Store().allTemporarilyHidden = true;
  }

  // "All" can never carry filters, so saving an "All*" spins off a NEW population (fresh
  // name + colour) and brings the untouched "All" back into the comparison.
  async function promoteEphemeral(populationId) {
    const resultsV2Store = useResultsV2Store();
    const pop = populations.value.find((p) => p.id === populationId && p.ephemeral);
    if (!pop) return null;

    const sg = pop.search_group;
    const name = !sg.name || sg.name === "All" ? nextGroupName() : sg.name;
    // Drop All's reserved colour so the new population gets its own comparison colour.
    const color = sg.color && sg.color !== POPULATION_AUTO_COLORS[0] ? sg.color : null;
    const saved = await resultsV2Store.createSavedPopulation({ name, filters: sg.filters, color });
    if (!saved) return null;

    if (resultsV2Store.isDefaultView) {
      const idx = populations.value.findIndex((p) => p.id === populationId);
      populations.value[idx] = { ...pop, id: `local_${saved.id}`, ephemeral: false, _fromAll: false, search_group: saved };
      return saved;
    }

    populations.value = populations.value.filter((p) => p.id !== populationId);
    await addPopulation({ search_group_id: saved.id });
    return saved;
  }

  return {
    populations,
    fetchPopulations,
    addPopulation,
    removePopulation,
    removeBySearchGroupId,
    resetForView,
    hasEphemeral,
    allQuickFilterActive,
    toggleFilterOnPopulation,
    hideAllAfterQuickFilter,
    isDirty,
    isNarrowed,
    resetPopulation,
    savePopulation,
    createEphemeral,
    updatePopulation,
    promoteEphemeral,
  };
});
